import CanvasContainer from "../../canvas/CanvasContainer.tsx";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { useEffect, useState, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import "./map.css";
import "./presentationMaker.css";
import { setSidenavColor } from "context/index.js";
import { setTransparentSidenav } from "context/index.js";
import { DarkMode } from "@mui/icons-material";
import { setWhiteSidenav } from "context/index.js";
import brandDark from "assets/images/logo-ct-dark.png";
import brandWhite from "assets/images/logo-ct.png";
import SideSlideNav from "examples/SideSlideNav/index.js";
import {
  AddSlideToPresentationCall,
  GetAllSlideWithContentByTitleId,
  AddContentToSlidePresentationCall,
  EditContentToSlidePresentationCall,
  DeletePresentationSlideCall,
} from "../../redux/api/universities.api.js";
import { useParams, useNavigate } from "react-router-dom";
import { store } from "../../redux/store.js";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentSlide, setSlides, updateCurrentSlideContent } from "../../redux/slices/uniSlice";
import MDButton from "components/MDButton";

function PresentationMakerPage() {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allSlides = useSelector((state) => state.uni?.slides);
  const isDark = theme.palette.mode === "dark";
  const currentSlide = useSelector((state) => state.uni?.currentSlide);
  const initialCanvasData = currentSlide?.content?.data
    ? (Array.isArray(currentSlide.content.data) ? currentSlide.content.data : [])
    : [];

  const hasValidId = id && id !== "undefined";

  useEffect(() => {
    if (!hasValidId) return;
    GetAllSlideWithContentByTitleId(id);
  }, [hasValidId, id]);

  useEffect(() => {
    if (hasValidId) return;
    navigate("/presentationTitles", { replace: true });
  }, [hasValidId, navigate]);

  const handleSaveSlide = async (canvasData) => {
    const slide = store.getState().uni?.currentSlide;
    if (!slide || !hasValidId) return;

    try {
      if (slide.presentation_content_id) {
        await EditContentToSlidePresentationCall({
          content_id: slide.presentation_content_id,
          data: canvasData,
        });
      } else {
        await AddContentToSlidePresentationCall({
          slide_id: slide.id,
          data: canvasData,
        });
      }
      // Update store with the data we just saved so the slide isn't replaced by a refetch
      // (refetch can return before the server has the latest, e.g. after zoom, and would reset the slide)
      dispatch(updateCurrentSlideContent(canvasData));
      await GetAllSlideWithContentByTitleId(id);
      // Don't replace currentSlide with refetched slide – we already updated it above
    } catch (error) {
      console.error("Error saving slide content:", error);
    }
  };

  const handleSlideSelect = (slide) => {
    dispatch(setCurrentSlide(slide));
  };

  const handleReorderSlides = (newSlides) => {
    dispatch(setSlides(newSlides));
  };

  const handleDeleteSlide = async (slide) => {
    if (!hasValidId || !slide?.id) return;
    if (!window.confirm("Delete this slide from the presentation?")) return;
    const deletedId = slide.id;
    const res = await DeletePresentationSlideCall(slide.id, id);
    if (res?.status === 200 || res?.status === 201) {
      if (currentSlide?.id === deletedId) {
        const updatedSlides = store.getState().uni?.slides ?? [];
        dispatch(setCurrentSlide(updatedSlides[0] ?? null));
      }
    }
  };

  const onMousePlus = async () => {
    if (!hasValidId) return;
    const university = store.getState()?.auth?.universityInfo;
    const body = {
      id,
      title_id: id,
      title: 1,
      university_id: university?.university_id,
    };
    AddSlideToPresentationCall(body);
  };

  const handleOnMouseLeave = () => {};

  const [isPlayMode, setIsPlayMode] = useState(false);
  const [playSlideIndex, setPlaySlideIndex] = useState(0);
  const slideBeforePlayRef = useRef(null);

  const startPlayMode = useCallback(() => {
    if (!allSlides?.length) return;
    slideBeforePlayRef.current = currentSlide;
    setIsPlayMode(true);
    setPlaySlideIndex(0);
  }, [allSlides?.length, currentSlide]);

  const exitPlayMode = useCallback(() => {
    setIsPlayMode(false);
    if (slideBeforePlayRef.current) {
      dispatch(setCurrentSlide(slideBeforePlayRef.current));
    }
    slideBeforePlayRef.current = null;
  }, [dispatch]);

  useEffect(() => {
    if (!isPlayMode) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        exitPlayMode();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (playSlideIndex < allSlides.length - 1) {
          setPlaySlideIndex((i) => i + 1);
        } else {
          exitPlayMode();
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (playSlideIndex > 0) {
          setPlaySlideIndex((i) => Math.max(0, i - 1));
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPlayMode, exitPlayMode, playSlideIndex, allSlides?.length]);

  const handlePlayOverlayClick = useCallback(() => {
    if (playSlideIndex < allSlides.length - 1) {
      setPlaySlideIndex((i) => i + 1);
    } else {
      exitPlayMode();
    }
  }, [playSlideIndex, allSlides?.length, exitPlayMode]);

  const playSlide = isPlayMode ? allSlides?.[playSlideIndex] : null;
  const playSlideData = playSlide?.content?.data;
  const playInitialData = Array.isArray(playSlideData) ? playSlideData : [];

  if (!hasValidId) {
    return null;
  }

  return (
    <DashboardLayout>
      <SideSlideNav
        color={setSidenavColor}
        slidesEnabled={true}
        brand={
          (setTransparentSidenav && !DarkMode) || setWhiteSidenav
            ? brandDark
            : brandWhite
        }
        brandName="Mircolab"
        slides={allSlides}
        currentSlideId={currentSlide?.id}
        onMousePlus={onMousePlus}
        onMouseLeave={handleOnMouseLeave}
        onSlideSelect={handleSlideSelect}
        onDeleteSlide={handleDeleteSlide}
        onReorderSlides={handleReorderSlides}
      />

      <DashboardNavbar />
      <Box
        className="content presentation-maker-root"
        sx={{
          p: 0,
          bgcolor: isDark ? undefined : "background.default",
        }}
      >
        {!isPlayMode && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5, display: "flex", justifyContent: "flex-end" }}>
            <MDButton
              variant="contained"
              color="info"
              startIcon={<PlayArrowIcon />}
              onClick={startPlayMode}
              disabled={!allSlides?.length}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: (t) => t.shadows[4],
                px: 2.5,
                "&:hover": { boxShadow: (t) => t.shadows[6] },
              }}
            >
              Play slides
            </MDButton>
          </Box>
        )}
        <Box className="presentation-maker-content">
          <Box className="presentation-canvas-wrapper">
            {!isPlayMode && (
              <CanvasContainer
                key={currentSlide?.id ?? "no-slide"}
                initialData={initialCanvasData}
                uploadEntity={{ entity_type: "presentation", entity_id: id }}
                onSave={handleSaveSlide}
              />
            )}
          </Box>
        </Box>

        {isPlayMode && (
          <Box
            onClick={handlePlayOverlayClick}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              bgcolor: "rgba(0,0,0,0.85)",
              display: "flex",
              cursor: "pointer",
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePlayOverlayClick();
              }
            }}
            aria-label="Click or press key to go to next slide"
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "background.default",
                pointerEvents: "none",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CanvasContainer
                key={playSlide?.id ?? playSlideIndex}
                initialData={playInitialData}
                isPlayMode
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                pointerEvents: "none",
                color: "rgba(255,255,255,0.8)",
                fontSize: 14,
              }}
            >
              {playSlideIndex + 1} / {allSlides?.length ?? 0}
              {playSlideIndex < (allSlides?.length ?? 0) - 1
                ? " — Click for next"
                : " — Click to close"}
            </Box>
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
}

export default PresentationMakerPage;
